import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  query,
  orderBy,
  CollectionReference,
  getDocs,
  limit,
  doc,
  getDoc,
  onSnapshot,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Draw, ResponseSD } from '../models/lottery.model';
import { GameType } from '../models/game.model';

@Injectable({
  providedIn: 'root',
})
export class DrawsAvailablesService {
  private collection = 'drawsAvailables';

  constructor(public fs: AngularFirestore) {}

  getAvailableDrawsRealtime(game?: GameType): Observable<Draw[] | undefined> {
    const docRef = doc(this.fs.firestore, this.collection, 'currentAvailableDraws');

    return new Observable<Draw[] | undefined>((observer) => {
      const unsubscribe = onSnapshot(
        docRef,
        (docSnapshot) => {
          if (!docSnapshot.exists()) {
            //console.log('No hay sorteos disponibles');
            observer.next(undefined);
            return;
          }

          const data = docSnapshot.data() as any;

          if (
            !data ||
            data.availableDraws.lnResponseSD.responseHeadOV.respCodeDescription !==
              'SD20A Sorteo(s) disponible(s) para su venta'
          ) {
            //console.log('Estructura de datos no válida', data);
            observer.next(undefined);
            return;
          } else {
            const formatted = this.formatDraws(data, game || '');
            observer.next(formatted);
          }
        },
        (error) => {
          console.error('Error al escuchar sorteos disponibles', error);
          observer.error(error);
        }
      );

      // Clean up the listener
      return () => unsubscribe();
    });
  }

  async getAvailableDraws(game?: GameType): Promise<Draw[] | undefined> {
    // Cambiamos la consulta para obtener directamente el documento con ID fijo
    const docRef = doc(this.fs.firestore, this.collection, 'currentAvailableDraws');

    try {
      const docSnapshot = await getDoc(docRef);
      if (!docSnapshot.exists()) {
        //console.log('No hay sorteos disponibles');
        return undefined;
      }

      const data = docSnapshot.data();
      const format = this.formatDraws(data, game || '');
      return format;
    } catch (error) {
      console.error('Error al obtener los sorteos disponibles', error);
      return [];
    }
  }

  getAllGamesAvailable(): Observable<Draw[]> {
    return this.fs.collection<Draw>(this.collection).valueChanges({ idField: 'id' });
  }

  getCurrentAvailableDrawsRealtime(): Observable<Record<string, unknown> | undefined> {
    const docRef = doc(this.fs.firestore, this.collection, 'currentAvailableDraws');

    return new Observable<Record<string, unknown> | undefined>((observer) => {
      const unsubscribe = onSnapshot(
        docRef,
        (docSnapshot) => {
          if (!docSnapshot.exists()) {
            observer.next(undefined);
            return;
          }

          observer.next(docSnapshot.data() as Record<string, unknown>);
        },
        (error) => {
          console.error('Error al escuchar sorteos disponibles (home)', error);
          observer.error(error);
        }
      );

      return () => unsubscribe();
    });
  }

  isDrawsResponseAvailable(data: Record<string, unknown> | undefined): boolean {
    const description = (data?.['availableDraws'] as ResponseSD | undefined)?.lnResponseSD?.responseHeadOV
      ?.respCodeDescription;

    return description === 'SD20A Sorteo(s) disponible(s) para su venta';
  }

  formatAllSorteos(data: Record<string, unknown> | undefined): Draw[] {
    const sorteos = (data?.['availableDraws'] as ResponseSD | undefined)?.lnResponseSD?.sorteo;

    if (!Array.isArray(sorteos)) {
      return [];
    }

    return sorteos.map((sorteo) => this.mapSorteo(sorteo));
  }

  async getAvailableDrawsHome(): Promise<Draw[] | undefined> {
    // Cambiamos la consulta para obtener directamente el documento con ID fijo
    const docRef = doc(this.fs.firestore, this.collection, 'currentAvailableDraws');

    try {
      const docSnapshot = await getDoc(docRef);
      if (!docSnapshot.exists()) {
        //console.log('No hay sorteos disponibles');
        return undefined;
      }

      const data = docSnapshot.data();
      return data['availableDraws'];
    } catch (error) {
      console.error('Error al obtener los sorteos disponibles', error);
      return [];
    }
  }

  formatDraws(data: any, gameType: string): Draw[] {
    const gameTypeUpper = gameType.toUpperCase().replace(/-/g, ' ');
    return data.availableDraws.lnResponseSD.sorteo
      .filter((sorteo: any) => {
        return sorteo.nombreSorteo.toUpperCase() === gameTypeUpper;
      })
      .map((sorteo: any) => this.mapSorteo(sorteo));
  }

  private mapSorteo(sorteo: any): Draw {
    return {
      idDotacion: sorteo.idDotacion,
      tipoSorteo: sorteo.tipoSorteo,
      numeroSorteo: sorteo.numeroSorteo,
      fechaCelebracion: sorteo.fechaCelebracion,
      nombreSorteo: sorteo.nombreSorteo,
      precioFraccion: sorteo.precioFraccion,
      digitosBillete: sorteo.digitosBillete,
      digitosTerminacion: sorteo.digitosTerminacion,
      codigoDotacion: sorteo.codigoDotacion,
      limiteInfFechaCierre: sorteo.limiteInfFechaCierre,
      limiteSupFechaCierre: sorteo.limiteSupFechaCierre,
      signos: sorteo.signo
        ? sorteo.signo.map((s: any) => ({
            idSigno: s.idSigno,
            nombreSigno: s.nombreSigno,
          }))
        : undefined,
    };
  }
}
